import { Component, Prop, Ref, Vue } from "vue-property-decorator";
import Loading from "@/components/loading/loading.vue";
import { Modal, Spin } from "ant-design-vue";
import { mergeClasses } from "@/util/func";
import { MediaPlayerContentType, MediaType } from "@/util/enum";
import { Fullscreen } from "@/util/fullscreen";
import enableInlineVideo from "iphone-inline-video";
import videojs from "video.js";
import "video.js/dist/video-js.css";

interface AudioSubtitleLine {
	Sequence: number;
	StartTime: string;
	EndTime: string;
	LineText: string;
	Speaker: string;
}

@Component({
	components: {
		Loading,
		Modal,
		Spin
	}
})
export default class VideoJSPlayer extends Vue {
	@Prop({ default: false }) loading: boolean = false;
	@Ref("playerWrapper") playerWrapper!: HTMLDivElement;

	public visible: boolean = false;
	public videoId: string = "";
	public title: string = "";
	public mediaTypeId: MediaType | null = null;
	public audioNoPoster: boolean = true;
	public subtitles: AudioSubtitleLine[] | undefined = [];
	public isFullscreen: boolean | null = null;
	public errorMessage: string = "";

	public myPlayer;
	arrayOfPosters: any[] = [];
	onClose?: () => void = () => {};

	mounted() {
		if (this.myPlayer) {
			try {
				this.myPlayer.dispose();
				// eslint-disable-next-line no-empty
			} catch (e) {}
		}
	}

	closeModal() {
		this.visible = false;
		try {
			if (this.myPlayer) {
				this.myPlayer.off("fullscreenchange", this.onFullScreenChange.bind(this));
				this.myPlayer.dispose();
			}
			// eslint-disable-next-line no-empty
		} catch (e) {}
		const img = document.getElementsByClassName("temp-img");
		const imgList = Array.from(img);
		if (imgList.length > 0) {
			imgList.forEach((element) => {
				element.remove();
			});
		}
		this.onClose && this.onClose();
	}

	showModal(
		videoId: string,
		mediaTypeId: MediaType,
		title: string,
		subtitles?: AudioSubtitleLine[],
		errorMessage?: string
	) {
		this.visible = true;
		this.videoId = videoId;
		this.title = title;
		this.mediaTypeId = mediaTypeId;
		this.subtitles = subtitles;
		this.errorMessage = errorMessage || "";
		return true;
	}

	setAWSMediaPlayer(id: string, media: string, type: MediaPlayerContentType, onClose?: () => void) {
		const mediaStreamingUrl = media;
		const mediaElement = document.getElementById(id);
		if (mediaElement) {
			this.myPlayer = videojs(mediaElement, {
				controlBar: {
					volumePanel: {
						inline: false
					},
					children: [
						"playToggle",
						"currentTimeDisplay",
						"timeDivider",
						"durationDisplay",
						"progressControl",
						"volumePanel",
						"fullscreenToggle"
					]
				},
				nativeControlsForTouch: false,
				autoplay: true,
				logo: { enabled: false },
				controls: true,
				width: "100%",
				height: !this.audioNoPoster ? "400" : "200",
				pictureInPictureToggle: false
			});
		}

		this.customVolumeTooltipText();

		this.myPlayer.src([
			{
				src: mediaStreamingUrl,
				type: type
			}
		]);
		this.addMediaAttributesForIOS(id);
		this.setPlayerCustomFullscreen();
		this.onClose = onClose;
	}

	customVolumeTooltipText() {
		const volumeTooltip = this.myPlayer
			.getChild("controlBar")
			.getChild("volumePanel")
			.getChild("volumeControl")
			.getChild("volumeBar")
			.getChild("mouseVolumeLevelDisplay")
			.getChild("volumeLevelTooltip");

		volumeTooltip.update = function (rangeBarRect, rangeBarPoint, vertical, volume) {
			this.write(`volume (${volume}%)`);
		};
	}

	addMediaAttributesForIOS(mediaId) {
		const media: HTMLDivElement = document.getElementById(mediaId) as HTMLDivElement;
		if (media) {
			enableInlineVideo(media);
			enableInlineVideo(media, { iPad: true });
		}
	}

	setPlayerCustomFullscreen() {
		this.myPlayer?.on("loadedmetadata", () => {
			const fullscreenTrigger = document.querySelector(".vjs-fullscreen-control");
			if (fullscreenTrigger) {
				const newFullscreenTrigger = fullscreenTrigger.cloneNode(true);
				newFullscreenTrigger.addEventListener("click", this.onClickCustomFullscreen);
				fullscreenTrigger.replaceWith(newFullscreenTrigger);
			}
		});
		(this.$refs.playerWrapper as HTMLDivElement).addEventListener("fullscreenchange", () =>
			this.onCustomFullscreenChange()
		);
	}

	onClickCustomFullscreen(event: Event) {
		const isFullscreen = Fullscreen.isActive();
		if (isFullscreen) {
			Fullscreen.exit(() => this.onCustomFullscreenChange(false));
		} else {
			const wrapper = this.$refs.playerWrapper;
			Fullscreen.open(wrapper as HTMLDivElement);
			this.onCustomFullscreenChange(true);
		}
		this.onFullScreenChange(event);
	}

	onCustomFullscreenChange(isFullscreen: boolean = Fullscreen.isActive()) {
		const playerElement = document.querySelector(".videojsmediaplayer") as Element;
		if (isFullscreen) {
			playerElement.classList.add("vjs-fullscreen");
			this.isFullscreen = true;
		} else {
			playerElement.classList.remove("vjs-fullscreen");
			this.isFullscreen = false;
		}
	}

	onFullScreenChange(event: Event) {
		if (window.navigator.userAgent.match(/iPad/i) != null) {
			const videoContainer = document.getElementsByClassName("vjs-poster")[0];
			if (videoContainer && videoContainer.children.length > 1) {
				const posterElement = videoContainer.children[1] as HTMLDivElement;
				posterElement.style.height = "auto";
			}
		}
	}

	get modalClass() {
		return mergeClasses("video-modal", this.audioNoPoster && "audio-no-poster", "videoplayermodal");
	}

	get isMediaTypeAudio() {
		if (this.mediaTypeId && this.mediaTypeId == MediaType.MP3) {
			return true;
		} else if (this.mediaTypeId && this.mediaTypeId == MediaType.MP4) {
			return false;
		}
	}
}
